/* eslint-disable no-plusplus */
/* eslint-disable no-shadow */
import React, { useState, useRef, useEffect, useMemo } from 'react';

import Modal from 'react-bootstrap/Modal';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import * as pdfjs from 'pdfjs-dist';

import classNames from 'classnames';
import useCheckMobileScreen from '../../hooks/useCheckMobileScreen';
import ConfirmButton from '../../components/Buttons/ConfirmButton';
import File from './File';
import FilledButton from '../../components/Buttons/FilledButton';

import classes from './styles.module.scss';
import closeIcon from '../../assets/images/closeIcon.svg';
import uploadIcon from '../../assets/images/create-meeting/upload.svg';
import backIcon from '../../assets/images/video-player/back.svg';

// Set worker source for PDF.js
if (typeof window !== 'undefined') {
  pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
}

export default function UploadDocumentsModal({
  show,
  handleClose,
  onSubmit,
  initialFiles,
  withText,
  readOnly,
}) {
  const [files, setFiles] = useState([]);
  const [prevFiles, setPrevFiles] = useState([]);
  const [deletedPrevFilesIds, setDeletedPrevFilesIds] = useState([]);
  const [description, setDescription] = useState('');
  const [page, setPage] = useState(3);
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);
  const [isPdfEnlarged, setIsPdfEnlarged] = useState(false);

  const inputRef = useRef();

  const isTabletOrMobile = useCheckMobileScreen();

  // Get all files (prevFiles + files)
  const allFiles = useMemo(() => {
    return [...(prevFiles || []), ...(files || [])];
  }, [prevFiles, files]);

  // Check if a file is a PDF
  const isPdfFile = (file) => {
    const fileName = file.name || file.fileName || '';
    const fileType = file.type || file.mimeType || '';
    const fileUrl = file.signedS3Url || file.url || '';
    
    return (
      fileType === 'application/pdf' ||
      (typeof fileName === 'string' && fileName.toLowerCase().endsWith('.pdf')) ||
      (typeof fileUrl === 'string' && fileUrl.toLowerCase().endsWith('.pdf'))
    );
  };

  // Get PDF files
  const pdfFiles = useMemo(() => {
    return allFiles.filter(isPdfFile);
  }, [allFiles]);

  // Check if we should show PDF directly (only one file and it's a PDF)
  const shouldShowPdfDirectly = useMemo(() => {
    return readOnly && allFiles.length === 1 && pdfFiles.length === 1 && isPdfFile(allFiles[0]);
  }, [readOnly, allFiles, pdfFiles]);

  // Get the PDF file to display (either the single PDF or the selected one)
  const pdfFileToDisplay = useMemo(() => {
    if (shouldShowPdfDirectly) {
      return allFiles[0];
    }
    return selectedPdfFile;
  }, [shouldShowPdfDirectly, allFiles, selectedPdfFile]);

  // Get PDF URL for a file
  const getPdfUrl = (file) => {
    return file.signedS3Url || file.url || '';
  };

  const openFileDialog = () => {
    inputRef.current.click();
  };

  const addFiles = (event) => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ];

    const addedFiles = Array.from(event.target.files);
    const filteredFiles = addedFiles
      .filter((file) => allowedTypes.includes(file.type))
      .filter((file) => {
        return (
          !files.some((prevFile) => prevFile.name === file.name) &&
          !prevFiles.some((pFile) => pFile.name === file.name)
        );
      });

    setFiles((prev) => {
      return [...prev, ...filteredFiles];
    });
  };

  useEffect(() => {
    if (!show) {
      setFiles([]);
      setPrevFiles([]);
      setDeletedPrevFilesIds([]);
      setDescription('');
      setSelectedPdfFile(null);
      setIsPdfEnlarged(false);
    }
  }, [show]);

  useEffect(() => {
    if (initialFiles && initialFiles.length > 0) {
      setPrevFiles(initialFiles);
    }
  }, [initialFiles, show]);

  const noFiles =
    (!files || files.length === 0) && (!prevFiles || prevFiles.length === 0) && !pdfFileToDisplay;

  const hasMoreFiles =
    prevFiles && prevFiles.length > 0 && page * 5 < prevFiles.length;

  return (
    <Modal
      show={show}
      centered
      contentClassName={classes.UploadDocumentsModal}
      dialogClassName={classes.dialog}
      backdropClassName={classes.backdrop}
    >
      <header className={classes.header}>
        <div className={classes.headerContent}>
          <div className={classes.titleRow}>
            <h1 className={classes.title}>Attachments</h1>
            <button
              className={classNames(classes.closeButton, classes.closeButtonMobile)}
              type="button"
              onClick={() => handleClose({ shouldContinue: false })}
            >
              <img src={closeIcon} alt="Close" />
            </button>
          </div>
          {pdfFileToDisplay && isPdfFile(pdfFileToDisplay) && (
            <h2 className={classes.fileName}>
              <a
                href={getPdfUrl(pdfFileToDisplay)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {pdfFileToDisplay.name || pdfFileToDisplay.fileName || 'PDF Document'}
              </a>
            </h2>
          )}
        </div>
        <button
          className={classNames(classes.closeButton, classes.closeButtonDesktop)}
          type="button"
          onClick={() => handleClose({ shouldContinue: false })}
        >
          <img src={closeIcon} alt="Close" />
        </button>
      </header>
      <div
        className={classNames(classes.attachments, {
          [classes.withText]: withText,
          [classes.hasNoFiles]: noFiles,
          [classes.hasPdf]: pdfFileToDisplay && isPdfFile(pdfFileToDisplay),
        })}
      >
        {/* Show PDF viewer if we should display PDF directly or if a PDF is selected */}
        {pdfFileToDisplay && isPdfFile(pdfFileToDisplay) && (
          <div 
            className={classNames({
              [classes.pdfEnlarged]: isPdfEnlarged,
            })}
            style={{ 
              width: '100%', 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              position: 'relative'
            }}
          >
            {/* Only show back button if we're not showing PDF directly (i.e., user selected from list) */}
            {!shouldShowPdfDirectly && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: '19px',
                marginBottom: '16px'
              }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedPdfFile(null);
                  }}
                  style={{
                    padding: 0,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <img src={backIcon} alt="Back" style={{ height: '24px', width: 'auto' }} />
                </button>
              </div>
            )}
            <div style={{ 
              width: '100%', 
              height: shouldShowPdfDirectly ? '100%' : 'calc(100% - 40px)', 
              border: 'none',
              borderRadius: shouldShowPdfDirectly ? '0' : '8px',
              overflow: isPdfEnlarged ? 'auto' : 'hidden',
              backgroundColor: '#fff',
              position: 'relative',
              flex: 1,
              minHeight: 0
            }}>
              <Worker workerUrl={pdfjs.GlobalWorkerOptions.workerSrc}>
                <Viewer 
                  fileUrl={getPdfUrl(pdfFileToDisplay)}
                  defaultScale={isPdfEnlarged ? 1.0 : undefined}
                  key={isPdfEnlarged ? 'enlarged' : 'shrunk'}
                />
              </Worker>
              {/* Toggle enlarge/shrink button in top left */}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsPdfEnlarged(!isPdfEnlarged);
                }}
                style={{
                  position: 'absolute',
                  top: isTabletOrMobile ? '12px' : '16px',
                  left: isTabletOrMobile ? '12px' : '16px',
                  padding: isTabletOrMobile ? '6px 12px' : '8px 16px',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: '#fff',
                  borderRadius: '6px',
                  fontSize: isTabletOrMobile ? '12px' : '14px',
                  fontWeight: 500,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                }}
              >
                <svg
                  width={isTabletOrMobile ? '14' : '16'}
                  height={isTabletOrMobile ? '14' : '16'}
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {isPdfEnlarged ? (
                    // Shrink icon (minus/compress)
                    <>
                      <path
                        d="M4 8H12"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <path
                        d="M2 2L14 14M14 2L2 14"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </>
                  ) : (
                    // Enlarge icon (expand)
                    <>
                      <path
                        d="M8 2V14M2 8H14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </>
                  )}
                </svg>
                {isPdfEnlarged ? 'Fit to Screen' : 'Enlarge'}
              </button>
              {/* Download button in top right */}
              <a
                href={getPdfUrl(pdfFileToDisplay)}
                download
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  position: 'absolute',
                  top: isTabletOrMobile ? '12px' : '16px',
                  right: isTabletOrMobile ? '12px' : '16px',
                  padding: isTabletOrMobile ? '6px 12px' : '8px 16px',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: '#fff',
                  borderRadius: '6px',
                  fontSize: isTabletOrMobile ? '12px' : '14px',
                  fontWeight: 500,
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                }}
              >
                <svg
                  width={isTabletOrMobile ? '14' : '16'}
                  height={isTabletOrMobile ? '14' : '16'}
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 11L3 6H6V2H10V6H13L8 11Z"
                    fill="currentColor"
                  />
                  <path
                    d="M2 13V14H14V13H2Z"
                    fill="currentColor"
                  />
                </svg>
                Download
              </a>
            </div>
          </div>
        )}

        {/* Show file list if no PDF should be displayed directly */}
        {!pdfFileToDisplay && (
          <>
            {prevFiles && prevFiles.length > 0
              ? prevFiles
                  .slice(0, isTabletOrMobile ? page * 5 : prevFiles.length)
                  .map((file) => {
                    return (
                      <File
                        key={file.name || file.id}
                        file={file}
                        readOnly={readOnly}
                        onDelete={() => {
                          setPrevFiles((prev) =>
                            prev.filter((prevFile) => prevFile.id !== file.id)
                          );
                          setDeletedPrevFilesIds((prev) => {
                            return [...prev, file.id];
                          });
                        }}
                        onClick={() => {
                          if (readOnly && isPdfFile(file)) {
                            setSelectedPdfFile(file);
                          }
                        }}
                      />
                    );
                  })
              : null}

            {files.length > 0
              ? files.map((file) => {
                  return (
                    <File
                      key={file.name}
                      readOnly={readOnly}
                      file={file}
                      onDelete={() => {
                        setFiles((prev) =>
                          prev.filter((prevFile) => prevFile.name !== file.name)
                        );
                      }}
                      onClick={() => {
                        if (readOnly && isPdfFile(file)) {
                          setSelectedPdfFile(file);
                        }
                      }}
                    />
                  );
                })
              : null}
          </>
        )}

        {noFiles && (
          <div className={classes.uploader} onClick={openFileDialog}>
            <img src={uploadIcon} alt="Upload" />
            <p>Import files to attach within your video</p>
          </div>
        )}
        {hasMoreFiles && (
          <div className={classes.loadMore}>
            <button
              type="button"
              onClick={() => {
                if (page * 5 >= prevFiles.length) {
                  return;
                }

                setPage((prev) => prev + 1);
              }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.5 0V4.5H0V7.5H4.5V12H7.5V7.5H12V4.5H7.5V0H4.5Z"
                  fill="#F8585C"
                />
              </svg>
              Load 5 more files
            </button>
          </div>
        )}
      </div>

      <footer className={classes.footer}>
        <p className={classes.info}>
          Files must be in or converted to .pdf, .docx, .xslx or .pptx in order
          to attach
        </p>
        <div className={classes.buttons}>
          {!readOnly && (
            <button
              type="button"
              className={classes.importButton}
              onClick={openFileDialog}
            >
              Select file
            </button>
          )}
          {isTabletOrMobile ? (
            <FilledButton onClick={handleClose} style={{ width: '100%' }}>
              Done
            </FilledButton>
          ) : (
            <ConfirmButton
              style={{ height: 70, width: 70 }}
              onClick={() => {
                if (readOnly) {
                  handleClose();
                  return;
                }

                if (files.length || deletedPrevFilesIds.length || description) {
                  onSubmit({
                    files,
                    deletedInitialFilesIds: deletedPrevFilesIds,
                    text: description,
                  });
                }

                handleClose();
              }}
            />
          )}
        </div>
      </footer>
      <input
        onChange={addFiles}
        ref={inputRef}
        type="file"
        accept=".pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx"
        multiple
        style={{
          display: 'none',
        }}
      />
    </Modal>
  );
}
